import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiRepos } from '../../../contracts/ContractVars';
import { TimeSelector } from './TimeSelector';
import './customDatePicker';
import {
    validateDateTime,
    getNextAvailableBusinessDateTime
} from './dateTimeUtils';
import { toast } from 'react-toastify';
import './OrderVehicle.css';



// ---- Local date helpers to avoid UTC drift ----
const formatDateLocal = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const buildLocalDate = (dateStr, hourStr, minuteStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const hh = Number(hourStr);
  const mm = Number(minuteStr);
  return new Date(y, m - 1, d, hh, mm, 0, 0);
};
// ----------------------------------------------
const initialDateTime = getNextAvailableBusinessDateTime();

const OrderVehicle = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const editOrderId = searchParams.get('edit');
    const isEditMode = !!editOrderId;

    const [formData, setFormData] = useState({
        isDriver: false,
        driverName: '',
        driverEmail: '',
        mobile: '',
        vehicleType: '',
        poNumber: '',
        logistics: '',
        addressLine1: '',
        addressLine2: '',
        town: '',
        postCode: '',
        collectionAddressSameAsDelivery: true,
        collectionAddressLine1: '',
        collectionAddressLine2: '',
        collectionTown: '',
        collectionPostCode: '',
        notes: '',
        purpose: '',
        hireFromDate: initialDateTime.date,
        hireFromHour: initialDateTime.hour,
        hireFromMinute: initialDateTime.minute,
        hireToDate: initialDateTime.date,
        hireToHour: (parseInt(initialDateTime.hour) + 1).toString().padStart(2, '0'),
        hireToMinute: initialDateTime.minute
    });

    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [businessAddresses, setBusinessAddresses] = useState([]);
    const [showDeliveryAddress, setShowDeliveryAddress] = useState(false);
    const [showCollectionAddress, setShowCollectionAddress] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [orderFormNotes, setOrderFormNotes] = useState([]);
    const [showBusinessHours, setShowBusinessHours] = useState(false);

    // Function to check if a date is a weekend
    const isWeekend = (dateStr) => {
        const [y, m, d] = dateStr.split('-').map(Number);
        const date = new Date(y, m - 1, d, 12, 0, 0, 0);
        const day = date.getDay();
        return day === 0 || day === 6;
    };

    const getNextWeekday = (dateStr) => {
        const [y, m, d] = dateStr.split('-').map(Number);
        const date = new Date(y, m - 1, d, 12, 0, 0, 0);

        const day = date.getDay();
        if (day === 0) date.setDate(date.getDate() + 1);
        else if (day === 6) date.setDate(date.getDate() + 2);

        date.setHours(8, 30, 0, 0);
        return date;
    };

    const toggleBusinessHours = () => {
        setShowBusinessHours(!showBusinessHours);
    };

    const validateUKMobile = (phone) => {
        const regex = /^(?:(?:\+44)|(?:0))7\d{9}$/;
        return regex.test(phone.replace(/\s+/g, ''));
    };

    const handleFromDateChange = (newFromDate) => {
        // Check if weekend for Collection and adjust if needed
        if (formData.logistics === 'Collect' && isWeekend(newFromDate)) {
            const nextWeekday = getNextWeekday(newFromDate);
            const nextWeekdayStr = formatDateLocal(nextWeekday);

            toast.info('Collection is not available on weekends. Date adjusted to next available business day.');

            setFormData(prev => {
                const newHour = nextWeekday.getHours().toString().padStart(2, '0');
                const newMinute = nextWeekday.getMinutes().toString().padStart(2, '0');

                // Also update hire to date if needed
                let newToDate = prev.hireToDate;
                let newToHour = prev.hireToHour;
                let newToMinute = prev.hireToMinute;

                const toDateTime = buildLocalDate(prev.hireToDate, prev.hireToHour, prev.hireToMinute);
                const newFromDateTime = buildLocalDate(nextWeekdayStr, newHour, newMinute);

                if (newFromDateTime >= toDateTime || isWeekend(prev.hireToDate)) {
                    const newToDateTime = new Date(newFromDateTime);
                    newToDateTime.setHours(newFromDateTime.getHours() + 1);

                    newToDate = formatDateLocal(newToDateTime);
                    newToHour = newToDateTime.getHours().toString().padStart(2, '0');
                    newToMinute = newToDateTime.getMinutes().toString().padStart(2, '0');
                }

                return {
                    ...prev,
                    hireFromDate: nextWeekdayStr,
                    hireFromHour: newHour,
                    hireFromMinute: newMinute,
                    hireToDate: newToDate,
                    hireToHour: newToHour,
                    hireToMinute: newToMinute
                };
            });

            return;
        }

        // Normal date change handling
        setFormData(prev => {
            const fromDateTime = buildLocalDate(newFromDate, prev.hireFromHour, prev.hireFromMinute);
            const toDateTime = buildLocalDate(prev.hireToDate, prev.hireToHour, prev.hireToMinute);

            if (fromDateTime >= toDateTime) {
                const newToDateTime = new Date(fromDateTime);
                newToDateTime.setHours(fromDateTime.getHours() + 1);

                return {
                    ...prev,
                    hireFromDate: newFromDate,
                    hireToDate: formatDateLocal(newToDateTime),
                    hireToHour: newToDateTime.getHours().toString().padStart(2, '0'),
                    hireToMinute: newToDateTime.getMinutes().toString().padStart(2, '0')
                };
            }
            return { ...prev, hireFromDate: newFromDate };
        });
    };

    const handleFromTimeChange = (type, value) => {
        setFormData(prev => {
            const newFormData = { ...prev, [type]: value };
            const fromDateTime = buildLocalDate(prev.hireFromDate, type === 'hireFromHour' ? value : prev.hireFromHour, type === 'hireFromMinute' ? value : prev.hireFromMinute);
            const toDateTime = buildLocalDate(prev.hireToDate, prev.hireToHour, prev.hireToMinute);

            // Check for business hours if collection
            if (prev.logistics === 'Collect') {
                const hours = parseInt(type === 'hireFromHour' ? value : prev.hireFromHour);
                const minutes = parseInt(type === 'hireFromMinute' ? value : prev.hireFromMinute);

                const isBeforeOfficeHours = hours < 8 || (hours === 8 && minutes < 30);
                const isAfterOfficeHours = hours > 17 || (hours === 17 && minutes > 30);

                if (isBeforeOfficeHours) {
                    newFormData.hireFromHour = '08';
                    newFormData.hireFromMinute = '30';
                    toast.info('Collection is only available after 8:30 AM. Time adjusted.');
                } else if (isAfterOfficeHours) {
                    // Move to next day at 8:30 AM
                    const nextDay = new Date(prev.hireFromDate);
                    nextDay.setDate(nextDay.getDate() + 1);

                    // Check if next day is weekend
                    const nextDayOfWeek = nextDay.getDay();
                    if (nextDayOfWeek === 0) { // Sunday
                        nextDay.setDate(nextDay.getDate() + 1); // Move to Monday
                    } else if (nextDayOfWeek === 6) { // Saturday
                        nextDay.setDate(nextDay.getDate() + 2); // Move to Monday
                    }

                    newFormData.hireFromDate = formatDateLocal(nextDay);
                    newFormData.hireFromHour = '08';
                    newFormData.hireFromMinute = '30';

                    toast.info('Collection is only available until 5:30 PM. Time adjusted to next business day.');

                    // Update from date and time
                    fromDateTime.setTime(nextDay.getTime());
                    fromDateTime.setHours(8, 30, 0, 0);
                }
            }

            // Ensure "to" time is after "from" time
    // Recompute after any clamping/mutations
    const __recomputedFrom = buildLocalDate(
      newFormData.hireFromDate, newFormData.hireFromHour, newFormData.hireFromMinute
    );
    fromDateTime.setTime(__recomputedFrom.getTime());

            if (fromDateTime >= toDateTime) {
                const newToDateTime = new Date(fromDateTime);
                newToDateTime.setHours(fromDateTime.getHours() + 1);

                return {
                    ...newFormData,
                    hireToDate: formatDateLocal(newToDateTime),
                    hireToHour: newToDateTime.getHours().toString().padStart(2, '0'),
                    hireToMinute: newToDateTime.getMinutes().toString().padStart(2, '0')
                };
            }

            return newFormData;
        });
    };

    const handleToTimeChange = (type, value) => {
        setFormData(prev => {
            const newFormData = { ...prev, [type]: value };
            const fromDateTime = buildLocalDate(prev.hireFromDate, prev.hireFromHour, prev.hireFromMinute);
            const toDateTime = buildLocalDate(prev.hireToDate, type === 'hireToHour' ? value : prev.hireToHour, type === 'hireToMinute' ? value : prev.hireToMinute);

            // Validate that "Hire To" is after "Hire From"
            if (toDateTime <= fromDateTime) {
                toast.error('Hire To time must be after Hire From time.');
                return prev; // Prevent invalid update
            }

            return newFormData;
        });
    };


    const handleLogisticsChange = useCallback(async (value) => {
        setFormData(prev => ({
            ...prev,
            logistics: value,
            collectionAddressSameAsDelivery: value === 'Delivery' ? prev.collectionAddressSameAsDelivery : true,
            collectionAddressLine1: value === 'Delivery' ? prev.collectionAddressLine1 : '',
            collectionAddressLine2: value === 'Delivery' ? prev.collectionAddressLine2 : '',
            collectionTown: value === 'Delivery' ? prev.collectionTown : '',
            collectionPostCode: value === 'Delivery' ? prev.collectionPostCode : ''
        }));

        // If changing to Collection and current date is weekend, adjust date
        if (value === 'Collect' && isWeekend(formData.hireFromDate)) {
            const nextWeekday = getNextWeekday(formData.hireFromDate);
            const nextWeekdayStr = formatDateLocal(nextWeekday);

            toast.info('Collection is not available on weekends. Date adjusted to next available business day.');

            setFormData(prev => ({
                ...prev,
                logistics: value,
                hireFromDate: nextWeekdayStr,
                hireFromHour: '08',
                hireFromMinute: '30',
                // Also update To date if it was on a weekend
                hireToDate: isWeekend(prev.hireToDate) ?
                    formatDateLocal(nextWeekday) : prev.hireToDate,
                hireToHour: isWeekend(prev.hireToDate) ? '09' : prev.hireToHour,
                hireToMinute: isWeekend(prev.hireToDate) ? '30' : prev.hireToMinute
            }));
        }

        // Handle delivery address loading
        if (value === 'Delivery') {
            setShowDeliveryAddress(true);
            setShowCollectionAddress(!formData.collectionAddressSameAsDelivery);
            try {
                const businessId = currentUser?.businessId;
                if (!businessId) return;

                const response = await fetch(`${ApiRepos}business/${businessId}/addresses`, {
                    credentials: 'include'
                });
                if (!response.ok) throw new Error('Failed to fetch addresses');
                const data = await response.json();
                setBusinessAddresses(data);
            } catch (error) {
                console.error('Error fetching business addresses:', error);
                setError('Failed to load delivery addresses');
            }
        } else {
            setShowDeliveryAddress(false);
            setShowCollectionAddress(false);
        }
    }, [currentUser?.businessId, formData.collectionAddressSameAsDelivery, formData.hireFromDate]);

    // Split into two effects - one for initial data
    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                // Fetch all initial data in parallel
                const [userResponse, typesResponse, notesResponse] = await Promise.all([
                    fetch(`${ApiRepos}account/current-user`, { credentials: 'include' }),
                    fetch(`${ApiRepos}lookup/vehicletypes/active`, { credentials: 'include' }),
                    fetch(`${ApiRepos}contentmanagement/order-form-bottom`, { credentials: 'include' })
                ]);

                if (!userResponse.ok) throw new Error('Failed to fetch user data');
                if (!typesResponse.ok) throw new Error('Failed to fetch vehicle types');

                const userData = await userResponse.json();
                const typesData = await typesResponse.json();
                setCurrentUser(userData);
                setVehicleTypes(typesData);

                // Handle the notes content separately as it's optional
                if (notesResponse.ok) {
                    const notesData = await notesResponse.json();
                    setOrderFormNotes(notesData);
                }
            } catch (err) {
                setError(err.message);
                console.error('Error fetching initial data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
    }, []);

    // Separate effect for handling edit mode
    useEffect(() => {
        const loadOrderData = async () => {
            if (!isEditMode || !editOrderId) return;

            try {
                setLoading(true);
                const orderResponse = await fetch(`${ApiRepos}order/${editOrderId}`, { credentials: 'include' });
                if (!orderResponse.ok) throw new Error('Failed to fetch order details');
                const orderData = await orderResponse.json();

                const fromDate = new Date(orderData.hireFromDate);
                const toDate = new Date(orderData.hireToDate);

                const newFormData = {
                    isDriver: false,
                    driverName: orderData.driverName,
                    driverEmail: orderData.driverEmail,
                    mobile: orderData.mobile,
                    vehicleType: orderData.vehicleTypeId.toString(),
                    poNumber: orderData.poNumber || '',
                    logistics: orderData.logistics,
                    addressLine1: orderData.addressLine1 || '',
                    addressLine2: orderData.addressLine2 || '',
                    town: orderData.town || '',
                    postCode: orderData.postCode || '',
                    collectionAddressSameAsDelivery: orderData.collectionAddressSameAsDelivery ?? true,
                    collectionAddressLine1: orderData.collectionAddressLine1 || '',
                    collectionAddressLine2: orderData.collectionAddressLine2 || '',
                    collectionTown: orderData.collectionTown || '',
                    collectionPostCode: orderData.collectionPostCode || '',
                    notes: orderData.notes || '',
                    purpose: orderData.purpose || '',
                    hireFromDate: formatDateLocal(fromDate),
                    hireFromHour: orderData.hireFromHour,
                    hireFromMinute: orderData.hireFromMinute,
                    hireToDate: formatDateLocal(toDate),
                    hireToHour: orderData.hireToHour,
                    hireToMinute: orderData.hireToMinute
                };

                setFormData(newFormData);

                // Handle logistics after form data is set
                if (orderData.logistics === 'Delivery') {
                    setShowCollectionAddress(!(orderData.collectionAddressSameAsDelivery ?? true));
                    await handleLogisticsChange('Delivery');
                }
            } catch (err) {
                setError(err.message);
                console.error('Error fetching order data:', err);
            } finally {
                setLoading(false);
            }
        };

        loadOrderData();
    }, [editOrderId, isEditMode, handleLogisticsChange]);

    const handleDriverCheckboxChange = (e) => {
        const isChecked = e.target.checked;
        setFormData({
            ...formData,
            isDriver: isChecked,
            driverName: isChecked && currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : '',
            driverEmail: isChecked && currentUser ? currentUser.emailAddress : '',
            mobile: isChecked && currentUser ? currentUser.mobileNumber : ''
        });
    };

    const handleAddressSelection = (addressId) => {
        const selected = businessAddresses.find(addr => addr.id === parseInt(addressId));
        if (selected) {
            setFormData({
                ...formData,
                addressLine1: selected.addressLine1,
                addressLine2: selected.addressLine2 || '',
                town: selected.town,
                postCode: selected.postCode
            });
        }
    };

    const handleCollectionAddressToggle = (sameAsDelivery) => {
        setShowCollectionAddress(!sameAsDelivery);
        setFormData(prev => ({
            ...prev,
            collectionAddressSameAsDelivery: sameAsDelivery,
            collectionAddressLine1: sameAsDelivery ? '' : prev.collectionAddressLine1,
            collectionAddressLine2: sameAsDelivery ? '' : prev.collectionAddressLine2,
            collectionTown: sameAsDelivery ? '' : prev.collectionTown,
            collectionPostCode: sameAsDelivery ? '' : prev.collectionPostCode
        }));
    };

    const getBusinessRulesMessage = () => {
        if (!formData.logistics) return null;

        const fromDateTime = buildLocalDate(formData.hireFromDate, formData.hireFromHour, formData.hireFromMinute);
        const fromDay = fromDateTime.getDay();

        // Weekend messages
        if (fromDay === 0 || fromDay === 6) {
            if (formData.logistics === 'Collect') {
                return (
                    <div className="business-rules-notice">
                        <p><strong>Weekend Collection Notice:</strong> Our office is closed on weekends. Please collect the keys by Friday 5:00 PM at the latest. You can then pick up your vehicle during the weekend.</p>
                    </div>
                );
            } else if (formData.logistics === 'Delivery') {
                return (
                    <div className="business-rules-notice">
                        <p><strong>Weekend Delivery Notice:</strong> We'll deliver your vehicle on Friday for weekend use.</p>
                    </div>
                );
            }
        }

        return null;
    };

    const validateDateTimes = () => {
        const result = validateDateTime(
            formData.hireFromDate,
            formData.hireFromHour,
            formData.hireFromMinute,
            formData.hireToDate,
            formData.hireToHour,
            formData.hireToMinute,
            formData.logistics
        );

        if (!result.isValid) {
            setError(result.error);
        }

        return result.isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateUKMobile(formData.mobile)) {
            setError('Please enter a valid UK mobile number');
            return;
        }

        if (!formData.poNumber.trim()) {
            setError('Please enter a PO number');
            return;
        }

        if (!validateDateTimes()) {
            return;
        }

        const submitData = {
            driverName: formData.driverName,
            driverEmail: formData.driverEmail,
            mobile: formData.mobile,
            vehicleTypeId: parseInt(formData.vehicleType),
            poNumber: formData.poNumber,
            logistics: formData.logistics,
            addressLine1: formData.addressLine1,
            addressLine2: formData.addressLine2,
            town: formData.town,
            postCode: formData.postCode,
            collectionAddressSameAsDelivery: formData.logistics === 'Delivery' ? formData.collectionAddressSameAsDelivery : true,
            collectionAddressLine1: formData.logistics === 'Delivery' && !formData.collectionAddressSameAsDelivery ? formData.collectionAddressLine1 : '',
            collectionAddressLine2: formData.logistics === 'Delivery' && !formData.collectionAddressSameAsDelivery ? formData.collectionAddressLine2 : '',
            collectionTown: formData.logistics === 'Delivery' && !formData.collectionAddressSameAsDelivery ? formData.collectionTown : '',
            collectionPostCode: formData.logistics === 'Delivery' && !formData.collectionAddressSameAsDelivery ? formData.collectionPostCode : '',
            notes: formData.notes,
            purpose: formData.purpose,
            hireFromDate: formData.hireFromDate,
            hireFromHour: formData.hireFromHour,
            hireFromMinute: formData.hireFromMinute,
            hireToDate: formData.hireToDate,
            hireToHour: formData.hireToHour,
            hireToMinute: formData.hireToMinute
        };

        try {
            const url = isEditMode
                ? `${ApiRepos}order/${editOrderId}`
                : `${ApiRepos}order/order`;

            const method = isEditMode ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(submitData)
            });

            if (!response.ok) {
                throw new Error('Failed to submit order');
            }

            const data = await response.json();
            const savedOrder = data.data || data;
            const savedOrderId = savedOrder?.id || editOrderId;

            // Check for email errors and display debug info if any
            if (data.emailStatus && !data.emailStatus.success) {
                // Add email error details to the toast notification
                let emailErrorMessage = "Order created but there was an issue sending email notifications.";

                if (data.emailStatus.userEmailError) {
                    emailErrorMessage += `\nUser notification: ${data.emailStatus.userEmailError}`;
                }

                if (data.emailStatus.adminEmailErrors && data.emailStatus.adminEmailErrors.length > 0) {
                    emailErrorMessage += `\nAdmin notifications: ${data.emailStatus.adminEmailErrors.join(", ")}`;
                }

                // Include debug info if available
                if (data.emailStatus.debug) {
                    // Format and display debug info
                    const userEmailDebug = data.emailStatus.debug.userEmail;
                    if (userEmailDebug) {
                        console.log("Email Debug Info:", userEmailDebug);
                        emailErrorMessage += "\n\nEmail Diagnostic Information:";
                        Object.entries(userEmailDebug).forEach(([key, value]) => {
                            emailErrorMessage += `\n${key}: ${value}`;
                        });
                    }
                }

                toast.warning(emailErrorMessage, {
                    autoClose: false,  // Keep the notification visible until dismissed
                    closeOnClick: false
                });

                // Still navigate to order but with warning about email
                navigate(`/dashboard/orders/${savedOrderId}`);
            } else {
                // Normal success path
                toast.success(isEditMode ? 'Order updated successfully' : 'Order submitted successfully');
                navigate(`/dashboard/orders/${savedOrderId}`);
            }
        } catch (err) {
            toast.error('Failed to submit order: ' + err.message);
        }
    };

    // Function to generate an array of date strings for the next 60 days
    const getNextDays = (days) => {
        const dateArray = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        for (let i = 0; i < days; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() + i);
            dateArray.push(formatDateLocal(date));
        }

        return dateArray;
    };

    // Generate disabledDates array dynamically based on logistics
    const disabledDates = formData.logistics === 'Collect'
        ? getNextDays(60).filter(isWeekend)
        : [];

    const hireFromMinDate =
        isEditMode && formData.hireFromDate < initialDateTime.date
            ? formData.hireFromDate
            : initialDateTime.date;

    // Apply minMinute only when SAME date AND SAME hour as the min boundary
    const effectiveMinMinute = (date, hour, minDate, minHour, minMinute) => {
        const sameDate = date === minDate;
        const sameHour = String(hour).padStart(2, '0') === String(minHour).padStart(2, '0');
        return (sameDate && sameHour) ? String(minMinute).padStart(2, '0') : '00';
    };



    if (loading) return <div className="qr-loading">Loading...</div>;
    if (error) return <div className="qr-error">{error}</div>;

    return (
        <div className="qr-container">
            <div className="qr-form-wrapper">
                <h2 className="qr-title">{isEditMode ? `Update Order Ref ${editOrderId}` : 'Order Vehicle'}</h2>

                <form onSubmit={handleSubmit} className="qr-form">
                    <div className="qr-form-section">
                        {/* Driver checkbox and details section */}
                        <div className="qr-form-row">
                            <div className="qr-form-group">
                                <label className="qr-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={formData.isDriver}
                                        onChange={handleDriverCheckboxChange}
                                        className="qr-checkbox-input"
                                    />
                                    Tick if you will be the driver
                                </label>
                            </div>
                        </div>

                        <div className="qr-form-row">
                            <div className="qr-form-group">
                                <label>Driver Name *</label>
                                <input
                                    type="text"
                                    value={formData.driverName}
                                    onChange={(e) => setFormData({ ...formData, driverName: e.target.value })}
                                    required
                                    placeholder="Enter driver name"
                                    disabled={formData.isDriver}
                                />
                            </div>
                            <div className="qr-form-group">
                                <label>Driver Email *</label>
                                <input
                                    type="email"
                                    value={formData.driverEmail}
                                    onChange={(e) => setFormData({ ...formData, driverEmail: e.target.value })}
                                    required
                                    placeholder="Enter driver email"
                                    disabled={formData.isDriver}
                                />
                            </div>
                            <div className="qr-form-group">
                                <label>Mobile Number *</label>
                                <input
                                    type="tel"
                                    value={formData.mobile}
                                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                                    required
                                    placeholder="+44 7XXX XXX XXX"
                                    disabled={formData.isDriver}
                                />
                            </div>
                        </div>

                        {/* Vehicle and PO section */}
                        <div className="qr-form-row">
                            <div className="qr-form-group">
                                <label>Vehicle Type *</label>
                                <select
                                    value={formData.vehicleType}
                                    onChange={(e) => setFormData({ ...formData, vehicleType: e.target.value })}
                                    required
                                >
                                    <option value="">Select vehicle type...</option>
                                    {vehicleTypes.map(type => (
                                        <option key={type.id} value={type.id}>{type.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="qr-form-group">
                                <label>PO Number *</label>
                                <input
                                    type="text"
                                    value={formData.poNumber}
                                    onChange={(e) => setFormData({ ...formData, poNumber: e.target.value })}
                                    required
                                    placeholder="PO number if available"
                                />
                            </div>
                        </div>

                        {/* Logistics field - placed before dates to ensure weekend restrictions are applied properly */}
                        <div className="qr-form-row">
                            <div className="qr-form-group">
                                <label>Logistics *</label>
                                <select
                                    value={formData.logistics}
                                    onChange={(e) => handleLogisticsChange(e.target.value)}
                                    required
                                >
                                    <option value="">Select logistics option...</option>
                                    <option value="Collect">Collect from SMC office</option>
                                    <option value="Delivery">Delivery</option>
                                </select>
                            </div>
                        </div>

                        {formData.logistics === 'Collect' && (
                            <div className="date-restriction-notice">
                                <strong>Note:</strong> Collection is only available on weekdays (Monday-Friday) between 8:30 AM and 5:30 PM.
                                Weekend dates will be disabled or automatically adjusted to the next available business day.
                            </div>
                        )}


                        {/* Business Hours Toggle Link and Section */}
                        <div className="business-hours-toggle">
                            <button
                                type="button"
                                onClick={toggleBusinessHours}
                                className="business-hours-toggle-link"
                            >
                                {showBusinessHours ? 'Hide our business hours' : 'View our business hours'}
                            </button>
                            {showBusinessHours && (
                                <div className={`business-hours ${showBusinessHours ? 'expanded' : ''}`}>
                                    <h3>Business Hours</h3>
                                    <div className="hours-table">
                                        <div className="hours-row">
                                            <span className="day">Monday - Friday:</span>
                                            <span className="time">8:30 am - 5:30 pm</span>
                                        </div>
                                        <div className="hours-row">
                                            <span className="day">Saturday:</span>
                                            <span className="time">Closed</span>
                                        </div>
                                        <div className="hours-row">
                                            <span className="day">Sunday:</span>
                                            <span className="time">Closed</span>
                                        </div>
                                    </div>
                                    <p className="hours-note">
                                        <strong>Note:</strong> Weekend pickups must be arranged by Friday 5:00 PM.
                                        Returns on weekends can be done by posting keys through our letterbox.
                                    </p>
                                </div>
                            )}
                        </div>



                        {/* Updated dates and times section */}
                        <div className="qr-form-row">
                            <div className="qr-form-group">
                                <label>Hire From *</label>
                                <div className="date-time-group">
                                    <input
                                        type="date"
                                        value={formData.hireFromDate}
                                        onChange={(e) => handleFromDateChange(e.target.value)}
                                        min={hireFromMinDate}
                                        required
                                        className="qr-date-input"
                                        // Manually add disabled dates via datalist
                                        {...(disabledDates.length > 0 && {
                                            onFocus: (e) => {
                                                // Add a custom attribute to indicate that weekend dates shouldn't be selectable
                                                e.target.setAttribute('data-restrict-weekends', 'true');
                                            }
                                        })}
                                    />
                                    <TimeSelector
                                        selectedHour={formData.hireFromHour}
                                        selectedMinute={formData.hireFromMinute}
                                        onHourChange={(value) => handleFromTimeChange('hireFromHour', value)}
                                        onMinuteChange={(value) => handleFromTimeChange('hireFromMinute', value)}
                                        date={formData.hireFromDate}
                                        minDate={hireFromMinDate}
                                        minHour={initialDateTime.hour}
                                        minMinute={effectiveMinMinute(
                                            formData.hireFromDate,
                                            formData.hireFromHour,
                                            hireFromMinDate,
                                            initialDateTime.hour,
                                            initialDateTime.minute
                                        )}
                                    />

                                </div>
                            </div>
                            <div className="qr-form-group">
                                <label>Hire To *</label>
                                <div className="date-time-group">
                                    <input
                                        type="date"
                                        value={formData.hireToDate}
                                        onChange={(e) => {
                                            const newToDate = e.target.value;
                                            setFormData(prev => {
                                                const fromDateTime = buildLocalDate(prev.hireFromDate, prev.hireFromHour, prev.hireFromMinute);
                                                const toDateTime = buildLocalDate(newToDate, prev.hireToHour, prev.hireToMinute);
                                                if (toDateTime <= fromDateTime) {
                                                    toast.error('Hire To date must be after Hire From date.');
                                                    return prev;
                                                }
                                                return { ...prev, hireToDate: newToDate };
                                            });
                                        }}
                                        min={formData.hireFromDate}
                                        required
                                        className="qr-date-input"
                                    />
                                    <TimeSelector
                                        selectedHour={formData.hireToHour}
                                        selectedMinute={formData.hireToMinute}
                                        onHourChange={(value) => handleToTimeChange('hireToHour', value)}
                                        onMinuteChange={(value) => handleToTimeChange('hireToMinute', value)}
                                        date={formData.hireToDate}
                                        minDate={formData.hireFromDate}
                                        minHour={formData.hireToDate === formData.hireFromDate ? formData.hireFromHour : '00'}
                                        minMinute={
                                            formData.hireToDate === formData.hireFromDate && formData.hireToHour === formData.hireFromHour
                                                ? formData.hireFromMinute
                                                : '00'
                                        }
                                    />
                                </div>
                            </div>
                        </div>

                        {formData.logistics && getBusinessRulesMessage()}

                        {/* Delivery address fields integrated in the same section */}
                        {showDeliveryAddress && (
                            <>
                                <div className="qr-form-row">
                                    <div className="qr-form-group">
                                        <label>Select Existing Address</label>
                                        <select
                                            onChange={(e) => handleAddressSelection(e.target.value)}
                                        >
                                            <option value="">Select an existing address or enter new</option>
                                            {businessAddresses.map(addr => (
                                                <option key={addr.id} value={addr.id}>
                                                    {addr.addressLine1}, {addr.town}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="qr-form-row">
                                    <div className="qr-form-group">
                                        <label className="qr-checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={formData.collectionAddressSameAsDelivery}
                                                onChange={(e) => handleCollectionAddressToggle(e.target.checked)}
                                                className="qr-checkbox-input"
                                            />
                                            Collection address is the same as delivery address
                                        </label>
                                    </div>
                                </div>

                                <div className="qr-form-row">
                                    <div className="qr-form-group">
                                        <label>Address Line 1 *</label>
                                        <input
                                            type="text"
                                            value={formData.addressLine1}
                                            onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                                            required
                                            placeholder="Enter address line 1"
                                        />
                                    </div>
                                    <div className="qr-form-group">
                                        <label>Address Line 2</label>
                                        <input
                                            type="text"
                                            value={formData.addressLine2}
                                            onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                                            placeholder="Enter address line 2"
                                        />
                                    </div>
                                </div>

                                <div className="qr-form-row">
                                    <div className="qr-form-group">
                                        <label>Town *</label>
                                        <input
                                            type="text"
                                            value={formData.town}
                                            onChange={(e) => setFormData({ ...formData, town: e.target.value })}
                                            required
                                            placeholder="Enter town"
                                        />
                                    </div>
                                    <div className="qr-form-group">
                                        <label>Post Code *</label>
                                        <input
                                            type="text"
                                            value={formData.postCode}
                                            onChange={(e) => setFormData({ ...formData, postCode: e.target.value })}
                                            required
                                            placeholder="Enter post code"
                                        />
                                    </div>
                                </div>

                                {showCollectionAddress && (
                                    <>
                                        <div className="qr-form-row">
                                            <div className="qr-form-group">
                                                <label>Collection Address Line 1 *</label>
                                                <input
                                                    type="text"
                                                    value={formData.collectionAddressLine1}
                                                    onChange={(e) => setFormData({ ...formData, collectionAddressLine1: e.target.value })}
                                                    required
                                                    placeholder="Enter collection address line 1"
                                                />
                                            </div>
                                            <div className="qr-form-group">
                                                <label>Collection Address Line 2</label>
                                                <input
                                                    type="text"
                                                    value={formData.collectionAddressLine2}
                                                    onChange={(e) => setFormData({ ...formData, collectionAddressLine2: e.target.value })}
                                                    placeholder="Enter collection address line 2"
                                                />
                                            </div>
                                        </div>

                                        <div className="qr-form-row">
                                            <div className="qr-form-group">
                                                <label>Collection Town *</label>
                                                <input
                                                    type="text"
                                                    value={formData.collectionTown}
                                                    onChange={(e) => setFormData({ ...formData, collectionTown: e.target.value })}
                                                    required
                                                    placeholder="Enter collection town"
                                                />
                                            </div>
                                            <div className="qr-form-group">
                                                <label>Collection Post Code *</label>
                                                <input
                                                    type="text"
                                                    value={formData.collectionPostCode}
                                                    onChange={(e) => setFormData({ ...formData, collectionPostCode: e.target.value })}
                                                    required
                                                    placeholder="Enter collection post code"
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        <div className="qr-form-group">
                            <label>Additional Notes</label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                rows={4}
                                placeholder="Enter additional notes or requirements"
                            />
                        </div>

                        <div className="qr-form-group">
                            <label>Purpose & Destination for Hire</label>
                            <textarea
                                value={formData.purpose}
                                onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                                rows={4}
                                placeholder="Enter purpose and destination"
                            />
                        </div>
                    </div>

                    <div className="qr-form-actions">
                        <button type="submit" className="qr-submit-button">
                            {isEditMode ? 'Update Order' : 'Order Vehicle'}
                        </button>
                    </div>
                    {orderFormNotes && orderFormNotes.length > 0 && (
                        <div className="qr-form-notes">
                            {orderFormNotes.map((note, index) => (
                                <div
                                    key={note.id}
                                    className="qr-note"
                                    dangerouslySetInnerHTML={{ __html: note.content }}
                                />
                            ))}
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default OrderVehicle;
